import React from 'react';
import styled from 'styled-components';
import Skeleton from '@/common/view/Skeleton';

const TableContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.gray20};
  border-radius: 8px;
  overflow: hidden;
`;

const HeaderRow = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.gray10};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray20};
  padding: 12px 16px;
  gap: 16px;
`;

const BodyRow = styled.div`
  display: flex;
  padding: 12px 16px;
  gap: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray10};
  
  &:last-child {
    border-bottom: none;
  }
`;

const Cell = styled.div<{ $flex?: number }>`
  flex: ${({ $flex }) => $flex || 1};
  display: flex;
  align-items: center;
`;

interface TableSkeletonProps {
  /** Number of columns */
  columns?: number;
  /** Number of rows */
  rows?: number;
  /** Column widths (flex values) */
  columnWidths?: number[];
}

/** Skeleton placeholder for table loading states */
const TableSkeleton: React.FC<TableSkeletonProps> = ({
  columns = 5,
  rows = 5,
  columnWidths,
}) => {
  const getColumnFlex = (index: number) => {
    if (columnWidths && columnWidths[index]) {
      return columnWidths[index];
    }
    return index === 0 ? 2 : 1;
  };

  return (
    <TableContainer>
      {/* Header */}
      <HeaderRow>
        {Array.from({ length: columns }).map((_, colIndex) => (
          <Cell key={`header-${colIndex}`} $flex={getColumnFlex(colIndex)}>
            <Skeleton width="80%" height={16} />
          </Cell>
        ))}
      </HeaderRow>
      
      {/* Body rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <BodyRow key={`row-${rowIndex}`}>
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Cell key={`cell-${rowIndex}-${colIndex}`} $flex={getColumnFlex(colIndex)}>
              <Skeleton 
                width={colIndex === 0 ? '70%' : '60%'} 
                height={14} 
              />
            </Cell>
          ))}
        </BodyRow>
      ))}
    </TableContainer>
  );
};

export default TableSkeleton;
